'use client';

import { ServiceLayer } from '@packages/cms-components/src/modules/ServiceLayer/ServiceLayer';
import { getFragmentData, type FragmentType } from '@packages/gql/generated/shopping';
import { ArticleServiceFragmentDoc } from '@packages/gql/generated/shopping/ArticleServiceFragmentDoc';
import { ArticleServicesCurrencyFragmentDoc } from '@packages/gql/generated/shopping/ArticleServicesCurrencyFragmentDoc';
import { selectedServices } from '@packages/modules/src/atoms';
import { useLayerTracking } from '@packages/modules/src/Tracking/hooks/useLayerTracking/useLayerTracking';
import { Dialog } from '@packages/shared';
import { Checkbox } from '@packages/shared/src/ui/Checkbox/Checkbox';
import { Image } from '@packages/shared/src/ui/Image/Image';
import { useSetAtom } from 'jotai';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { usePriceFormater } from '../../../hooks/usePriceFormater';
import { useArticleServicesTracking } from '../../Tracking/hooks/useArticleServicesTracking';
import { articleServicesMessages } from '../messages';

/* GraphQL */ `
  fragment ArticleService on Service {
    serviceIcon
    uuid
    name
    price
    gratis
    preselected
    mandatory
    orderId
    serviceLayerId
  }
`;

/* GraphQL */ `
  fragment ArticleServicesCurrency on ProductPrice {
    currency
  }
`;

export type ArticleServiceProps = {
  data: FragmentType<typeof ArticleServiceFragmentDoc>;
  /* should be in aPlus price value */
  currencyData: FragmentType<typeof ArticleServicesCurrencyFragmentDoc>;
};

/**
 * This component is part of the `ArticleServices` component. It displays a single service with:
 * <ul>
 *  <li>Checkbox</li>
 *  <li>Icon</li>
 *  <li>Service Name</li>
 *  <li>Price</li>
 *  <li>"Gratis/Inklusive" Text</li>
 *  <li>More Info Icon</li>
 *  <li>More Info Dialog</li>
 * </ul>
 * <i>Note: This component should only be used within the `ArticleServices` component.</i>
 */
export const ArticleService = ({ currencyData, data }: ArticleServiceProps) => {
  const {
    uuid,
    gratis,
    mandatory,
    serviceIcon,
    name,
    price,
    preselected,
    orderId,
    serviceLayerId,
  } = getFragmentData(ArticleServiceFragmentDoc, data);
  const { currency } = getFragmentData(ArticleServicesCurrencyFragmentDoc, currencyData);

  const [selected, setSelected] = useState(Boolean(preselected));
  const setAtomSelected = useSetAtom(selectedServices);

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { trackingGlycerinLinkClick } = useArticleServicesTracking();
  const { trackViewServiceLayer } = useLayerTracking();

  const { formatMessage } = useIntl();
  const formatPrice = usePriceFormater('APLUS');

  const handleMoreInfo = () => {
    trackingGlycerinLinkClick({
      category: uuid,
      detail: name || undefined,
    });
    trackViewServiceLayer(name);
    setIsDialogOpen(true);
  };

  useEffect(() => {
    setAtomSelected((prev) =>
      selected
        ? prev.concat({ sku: orderId, selected: true })
        : prev.filter((service) => service.sku !== orderId),
    );
  }, [selected, setAtomSelected, orderId]);

  return (
    <>
      <div className="flex py-1">
        {/* Checkmark or Checkbox item */}
        <div className="mr-1.5">
          {mandatory ? (
            <span className="icon-emp-checkmark text-color-success m-0.5 h-3 w-3" role="img" />
          ) : (
            <Checkbox
              checked={selected}
              onChange={() => setSelected((prev) => !prev)}
              id={uuid}
              aria-checked={selected}
            />
          )}
        </div>

        {/* Label with icon, description and price */}
        {/* Use dynamic component based on mandatory prop */}
        {React.createElement(
          !mandatory ? 'label' : 'div',
          {
            className: `flex grow items-start gap-1 ${!mandatory ? 'cursor-pointer' : ''}`,
            ...(!mandatory && { htmlFor: uuid }),
          },
          <>
            {/* service icon */}
            <Image src={`${serviceIcon}.svg`} alt="" width={32} height={32} unoptimized />
            {/* service mame */}
            {/* this margin is needed to place the name at the baseline of the first row to the center of the other item in this row */}
            <p className="text-size-body2 mt-[0.5em] grow hyphens-auto">{name}</p>
            {!gratis && price && (
              <p className="text-size-body2 leading-4 font-bold whitespace-nowrap">
                +{formatPrice(price, currency)}
              </p>
            )}
            {gratis && (
              <p className="text-size-body2 text-color-success-dark leading-4 font-bold whitespace-nowrap">
                {mandatory
                  ? formatMessage(articleServicesMessages.articleServicesInclusive)
                  : formatMessage(articleServicesMessages.articleServicesFree)}
              </p>
            )}
          </>,
        )}
        {/* more info button */}
        <button
          type="button"
          aria-haspopup="dialog"
          aria-label={formatMessage(articleServicesMessages.articleServicesMoreInfo)}
          className="ml-1 flex h-3 w-2.5 items-end"
          onClick={handleMoreInfo}
        >
          <span className="icon-emp-info-filled h-2.5 w-2.5" role="img" />
        </button>
      </div>
      {isDialogOpen && (
        <Dialog
          open={isDialogOpen}
          handleClose={() => setIsDialogOpen(false)}
          title={name || ''}
          dialogContent={<ServiceLayer serviceLayerId={serviceLayerId} />}
          displayTrackingProps={{
            category: uuid,
            detail: name || undefined,
            label: 'APlus Service',
          }}
        />
      )}
    </>
  );
};
